import React, { useEffect, useRef, useState } from 'react';
import { func, number, string } from 'prop-types';

import '../styles/PriceRange.css';

/**
 * Configuration
 */

const TIMEOUT_DURATION = 500;

const PriceRange = ({ minPrice, maxPrice, handlePriceChange, queryMinPrice, queryMaxPrice }) => {
    const minInputRef = useRef(null);
    const maxInputRef = useRef(null);
    const thumbLeftRef = useRef(null);
    const thumbRightRef = useRef(null);
    const rangeRef = useRef(null);
    const [selectedMinPrice, setSelectedMinPrice] = useState(minPrice);
    const [selectedMaxPrice, setSelectedMaxPrice] = useState(maxPrice);
    const [changedByUser, setChangedByUser] = useState(false);

    const [minPercentage, setMinPercentage] = useState(0);
    const [maxPercentage, setMaxPercentage] = useState(100);

    const onMinPercentageChange = (e) => {
        const newMinValue = Math.min(parseInt(e.target.value), parseInt(maxPercentage));
        const percent = ((newMinValue - 0) / (100 - 0)) * 100;
        const newSelectedMinPrice = Math.round(minPrice + (maxPrice - minPrice) * (percent / 100));

        setMinPercentage(percent);
        setSelectedMinPrice(newSelectedMinPrice);
        setChangedByUser(true);
    };

    const onMaxPercentageChange = (e) => {
        const newMaxValue = Math.max(parseInt(e.target.value), parseInt(minPercentage));
        const percent = ((newMaxValue - 0) / (100 - 0)) * 100;
        const newSelectedMaxPrice = Math.round(maxPrice - (maxPrice - minPrice) * ((100 - percent) / 100));

        setMaxPercentage(percent);
        setSelectedMaxPrice(newSelectedMaxPrice);
        setChangedByUser(true);
    };

    useEffect(() => {
        if (!changedByUser) {
            return;
        }

        const timerId = setTimeout(() => {
            handlePriceChange(selectedMinPrice, selectedMaxPrice);
        }, TIMEOUT_DURATION);

        return () => clearTimeout(timerId);
    }, [selectedMinPrice, selectedMaxPrice]);

    useEffect(() => {
        const selectedMinPrice = parseInt(queryMinPrice);
        const selectedMaxPrice = parseInt(queryMaxPrice);

        if (!maxPrice) {
            return;
        }

        if (!isNaN(selectedMinPrice)) {
            const percent = (selectedMinPrice - minPrice) / (maxPrice - minPrice) * 100;
            setMinPercentage(percent);
            setSelectedMinPrice(selectedMinPrice);
        } else {
            setMinPercentage(0);
            setSelectedMinPrice(parseInt(minPrice));
        }

        if (!isNaN(selectedMaxPrice)) {
            const percent = (selectedMaxPrice - minPrice) / (maxPrice - minPrice) * 100;
            setMaxPercentage(percent);
            setSelectedMaxPrice(selectedMaxPrice);
        } else {
            setMaxPercentage(100);
            setSelectedMaxPrice(maxPrice);
        }
    }, [maxPrice, queryMinPrice, queryMaxPrice]);

    if (!maxPrice) {
        return null;
    }

    return (
        <div
            className={'price-range'}
        >
            <div
                className={'price-range__inputs-range-container'}
            >
                <input
                    type={'range'}
                    id={'input-left'}
                    min={0}
                    max={100}
                    value={minPercentage}
                    ref={minInputRef}
                    onChange={onMinPercentageChange}
                />
                <input
                    type={'range'}
                    id={'input-right'}
                    min={0}
                    max={100}
                    value={maxPercentage}
                    ref={maxInputRef}
                    onChange={onMaxPercentageChange}
                />

                <div
                    className={'price-range__slider'}>
                    <div
                        className={'track'}></div>
                    <div
                        style={{
                            left: `${minPercentage}%`,
                            right: `${100 - maxPercentage}%`
                        }}
                        ref={rangeRef}
                        className={'range'}></div>
                    <div
                        style={{left: `${minPercentage}%` }}
                        ref={thumbLeftRef}
                        className={'thumb left'}></div>
                    <div
                        style={{right: `${100 - maxPercentage}%` }}
                        ref={thumbRightRef}
                        className={'thumb right'}></div>
                </div>
            </div>
            <div
                className={'price-range__prices'}
            >
                <div>${selectedMinPrice}</div>
                <div>${selectedMaxPrice}</div>
            </div>
        </div>
    );
};

PriceRange.propTypes = {
    minPrice: number,
    maxPrice: number,
    handlePriceChange: func,
    queryMinPrice: string,
    queryMaxPrice: string,
};

export default PriceRange;